import React, {useState} from "react";

const toArray = (value) => {
	if (Array.isArray(value)) return value;
	if (value === null || value === undefined || value === "") return [];
	return [value];
};

function StudioAdCard({item, isSaving, onToggle, onEdit, onDelete, onExport, isExporting = false}) {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const isActive = Boolean(item.active ?? item.is_active);
	const displayTitle = item.title || "Untitled ad";
	const slotCount = toArray(item.slot_ids).length || item.slots?.length || 0;
	const studioLabel = item.studio?.name || (item.studio_id ? `Studio #${item.studio_id}` : "Brand ad");
	const destination = item.destination_url || "Studio page";
	const ctr = Number.isFinite(Number(item.ctr)) ? `${Number(item.ctr).toFixed(2)}%` : "0%";
	const viewSeconds = Number.isFinite(Number(item.total_view_seconds)) ? Number(item.total_view_seconds).toFixed(2) : "0.00";

	const statusPill = (
		<span
			className={`rounded-full px-3 py-1 text-xs font-semibold ${
				isActive ? "bg-[#E8F8EF] text-[#198754]" : "bg-[#F3F4F6] text-[#6D6E6F]"
			}`}
		>
			{isActive ? "Active" : "Inactive"}
		</span>
	);

	const mobileToggle = (
		<button
			type="button"
			onClick={onToggle}
			disabled={isSaving}
			className={`relative inline-flex h-7 w-12 items-center rounded-full transition disabled:opacity-60 ${
				isActive ? "bg-[#31A762]" : "bg-[#D1D5DB]"
			}`}
			aria-pressed={isActive}
		>
			<span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${isActive ? "translate-x-6" : "translate-x-1"}`} />
		</button>
	);

	const metricItem = (label, value, icon) => (
		<div className="rounded-md border border-[#ECECEC] bg-[#FAFAFA] px-2 py-1.5">
			<div className="flex items-center gap-1 text-[#6D6E6F]">
				{icon}
				<span className="text-[11px]">{label}</span>
			</div>
			<p className="mt-1 text-sm font-semibold text-[#1E1F21]">{value}</p>
		</div>
	);

	return (
		<div className="overflow-hidden rounded-xl border border-[#E8E8E8] bg-white shadow-[0_6px_20px_rgba(0,0,0,0.05)]">
			<div className="relative h-40 w-full bg-[#EEF0F2]">
				{item.cover_url ? (
					<img src={item.cover_url} alt={displayTitle} className="h-full w-full object-cover" />
				) : (
					<div className="flex h-full w-full items-center justify-center text-sm font-medium text-[#6D6E6F]">No cover</div>
				)}
				<div className="absolute left-3 top-3">{mobileToggle}</div>
				<div className="absolute right-3 top-3">
					<div className="flex items-center gap-2">
						{statusPill}
						<button
							type="button"
							onClick={() => setIsMenuOpen((prev) => !prev)}
							aria-label="Open actions"
							className="flex h-8 w-8 items-center justify-center rounded-full border border-white/70 bg-white/90 text-[#4E5052] shadow-sm backdrop-blur transition hover:bg-white"
						>
							<svg width={16} height={16} viewBox="0 0 24 24" fill="none">
								<circle cx="12" cy="5" r="1.8" fill="currentColor" />
								<circle cx="12" cy="12" r="1.8" fill="currentColor" />
								<circle cx="12" cy="19" r="1.8" fill="currentColor" />
							</svg>
						</button>
						{isMenuOpen ? (
							<div className="absolute right-0 top-10 z-30 w-40 overflow-hidden rounded-xl border border-[#E7E4E2] bg-white shadow-[0_14px_30px_rgba(0,0,0,0.18)]">
								<button
									type="button"
									onClick={() => {
										onEdit();
										setIsMenuOpen(false);
									}}
									className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-[#2E3031] transition hover:bg-[#F6F7F8]"
								>
									<svg width={14} height={14} viewBox="0 0 24 24" fill="none"><path d="M4 20h4l10-10-4-4L4 16v4Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><path d="m12 6 4 4" stroke="currentColor" strokeWidth="2"/></svg>
									Edit
								</button>
								{onExport ? (
									<button
										type="button"
										onClick={() => onExport()}
										disabled={isExporting}
										className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-[#2E3031] transition hover:bg-[#F6F7F8] disabled:opacity-60"
									>
										<svg width={14} height={14} viewBox="0 0 24 24" fill="none"><path d="M12 3v11m0 0 4-4m-4 4-4-4M4 17v2h16v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
										{isExporting ? "Exporting..." : "Export CSV"}
									</button>
								) : null}
								<button
									type="button"
									onClick={() => {
										onDelete();
										setIsMenuOpen(false);
									}}
									className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-[#DC3545] transition hover:bg-[#FDF2F4]"
								>
									<svg width={14} height={14} viewBox="0 0 24 24" fill="none"><path d="M3 6h18M8 6V4h8v2m-1 0v14H9V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
									Delete
								</button>
							</div>
						) : null}
					</div>
				</div>
			</div>
			<div className="space-y-3 p-4">
				<div>
					<p className="text-base font-semibold text-[#1E1F21]">{displayTitle}</p>
					<p className="mt-1 text-sm text-[#6D6E6F]">ID: {item.id} • Priority: {item.priority ?? 0}</p>
				</div>
				<div className="grid grid-cols-2 gap-2 md:grid-cols-4">
					{metricItem("Impressions", item.impressions ?? 0, (
						<svg width={12} height={12} viewBox="0 0 24 24" fill="none"><path d="M1.5 12S5 5.5 12 5.5 22.5 12 22.5 12 19 18.5 12 18.5 1.5 12 1.5 12Z" stroke="currentColor" strokeWidth="2"/><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/></svg>
					))}
					{metricItem("Clicks", item.clicks ?? 0, (
						<svg width={12} height={12} viewBox="0 0 24 24" fill="none"><path d="M7 3v18l5-4 4 4 2-2-4-4 4-5H7Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/></svg>
					))}
					{metricItem("CTR", ctr, (
						<svg width={12} height={12} viewBox="0 0 24 24" fill="none"><path d="M4 20 20 4M7 4h13v13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
					))}
					{metricItem("View (s)", viewSeconds, (
						<svg width={12} height={12} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/><path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
					))}
				</div>
				<div className="grid grid-cols-1 gap-2 md:grid-cols-2">
					<div className="rounded-md border border-[#ECECEC] bg-[#FAFAFA] px-2 py-1.5">
						<p className="text-[11px] text-[#6D6E6F]">Slots ({slotCount})</p>
						<p className="truncate text-sm font-semibold text-[#1E1F21]">{item.slots?.map((slot) => slot.name).join(", ") || "N/A"}</p>
					</div>
					<div className="rounded-md border border-[#ECECEC] bg-[#FAFAFA] px-2 py-1.5">
						<p className="text-[11px] text-[#6D6E6F]">Destination / Target</p>
						<p className="truncate text-sm font-semibold text-[#1E1F21]">{destination === "Studio page" ? studioLabel : destination}</p>
					</div>
				</div>
			</div>
		</div>
	);
}

export default StudioAdCard;
